import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES, ROLE_DESCRIPTIONS, ROLE_LABELS, type Role } from '@aq/shared';
import { ApiError, api } from '../../lib/api.ts';
import { Alert, Button, Icons, Spinner, cx } from '../../components/ui.tsx';
import { ContextMenu, useContextMenu, type MenuItem } from '../../components/ContextMenu.tsx';
import { useDialog } from '../../components/Dialog.tsx';
import { useToast } from '../../components/Toast.tsx';
import { Tooltip } from '../../components/Tooltip.tsx';
import { formatDateTime, formatRelative, pluralise } from '../../lib/display.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { AccessPicker, type AccessSelection } from './AccessPicker.tsx';
import { itemKey, type PickableItem } from './folder-grants.ts';

/** What the grant endpoints take and give back. */
interface GrantPayload {
  folderIds: string[];
  items: Array<{ kind: PickableItem['kind']; id: string }>;
}

const EMPTY_SELECTION: AccessSelection = { folders: new Set(), items: new Map() };

/** The selection as the server wants it. */
function payloadOf(selection: AccessSelection): GrantPayload {
  return {
    folderIds: [...selection.folders],
    items: [...selection.items.values()].map((item) => ({ kind: item.kind, id: item.id })),
  };
}

/**
 * Managing accounts.
 *
 * Two rules from the server surface here rather than being discovered by
 * pressing a button that fails: an account with audit history cannot be
 * deleted, only deactivated, and the last active owner cannot be demoted or
 * switched off. Both are shown as state on the row.
 */

interface UserSummary {
  id: string;
  email: string;
  name: string;
  role: Role;
  isActive: boolean;
  isSuspended: boolean;
  totpEnabled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  canDelete: boolean;
  activeSessions: number;
}

export function UsersPage() {
  const { user: me } = useAuth();
  const dialog = useDialog();
  const toast = useToast();
  const client = useQueryClient();

  const query = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/api/users'),
  });

  const refresh = () => void client.invalidateQueries({ queryKey: ['users'] });

  const create = useMutation({
    mutationFn: (input: { email: string; name: string; role: Role } & GrantPayload) =>
      api.post<{ user: UserSummary; password: string }>('/api/users', input),
    onSuccess: refresh,
  });

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-body-sm text-muted">
        <Spinner /> Loading accounts…
      </div>
    );
  }

  if (query.isError) return <Alert>Could not load the accounts.</Alert>;

  const users = query.data ?? [];
  const activeOwners = users.filter((user) => user.role === 'OWNER' && user.isActive).length;

  function invite() {
    dialog.open(
      (close) => (
        <InviteDialog
          onClose={close}
          onSubmit={async (input) => {
            try {
              const result = await create.mutateAsync(input);
              close();
              void showPassword(result.user.email, result.password, 'Account created');
            } catch (caught) {
              toast.error(
                'Could not create the account',
                caught instanceof ApiError ? caught.message : 'Please try again.',
              );
            }
          }}
        />
      ),
      // The account beside the Drive it can see, not stacked above it.
      { wide: true },
    );
  }

  /**
   * Show a generated password once.
   *
   * A notify dialog rather than a toast: this is the only time the value
   * exists in readable form, and a box that disappears on its own would lose
   * it.
   */
  async function showPassword(email: string, password: string, title: string) {
    await dialog.notify({
      title,
      message: `Give this password to ${email}. It is shown once and cannot be retrieved — generate a new one if it is lost.\n\n${password}`,
    });
  }

  return (
    <div className="h-full overflow-y-auto">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
        <div>
          <h1 className="text-h1 text-ink">Users</h1>
          <p className="mt-0.5 text-meta text-muted">
            {pluralise(users.length, 'account')} · {activeOwners} active{' '}
            {activeOwners === 1 ? 'owner' : 'owners'}
          </p>
        </div>

        <Button variant="primary" icon={<Icons.Plus />} onClick={invite}>
          Add someone
        </Button>
      </header>

      <div className="mt-4 overflow-x-auto rounded-lg border border-border-strong bg-surface">
        <table className="w-full max-md:min-w-[560px]">
          <thead>
            <tr className="border-b border-border-strong bg-sunken">
              <Th>Name</Th>
              <Th className="w-40">Role</Th>
              <Th className="w-32">Status</Th>
              <Th className="w-48">Last signed in</Th>
              <Th className="w-20" align="right">
                Actions
              </Th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                isMe={user.id === me?.id}
                isLastOwner={user.role === 'OWNER' && user.isActive && activeOwners === 1}
                onChanged={refresh}
                onPassword={showPassword}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-sunken p-4">
        <h2 className="text-h3 text-ink">What the roles allow</h2>
        <dl className="mt-2 space-y-1.5">
          {ROLES.map((role) => (
            <div key={role} className="flex gap-3">
              <dt className="w-28 shrink-0 text-body-sm font-medium text-ink">
                {ROLE_LABELS[role]}
              </dt>
              <dd className="text-body-sm text-body">{ROLE_DESCRIPTIONS[role]}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

function UserRow({
  user,
  isMe,
  isLastOwner,
  onChanged,
  onPassword,
}: {
  user: UserSummary;
  isMe: boolean;
  isLastOwner: boolean;
  onChanged(): void;
  onPassword(email: string, password: string, title: string): void;
}) {
  const menu = useContextMenu();
  const dialog = useDialog();
  const toast = useToast();

  const run = async (
    work: () => Promise<unknown>,
    success: string,
    failure: string,
  ): Promise<void> => {
    try {
      await work();
      toast.success(success);
      onChanged();
    } catch (caught) {
      toast.error(failure, caught instanceof ApiError ? caught.message : 'Please try again.');
    }
  };

  async function changeRole(role: Role) {
    if (role === user.role) return;
    await run(
      () => api.post(`/api/users/${user.id}`, { role }),
      `${user.name} is now ${ROLE_LABELS[role].toLowerCase()}`,
      'Could not change the role',
    );
  }

  async function toggleActive() {
    const turningOff = user.isActive;
    if (turningOff) {
      const confirmed = await dialog.confirm({
        title: `Deactivate ${user.name}?`,
        message:
          'They are signed out everywhere immediately and cannot sign in again until the account is reactivated.',
        details: user.activeSessions > 0 ? [pluralise(user.activeSessions, 'active session')] : [],
        confirmLabel: 'Deactivate',
        tone: 'danger',
      });
      if (!confirmed) return;
    }

    await run(
      () => api.post(`/api/users/${user.id}/active`, { isActive: !user.isActive }),
      turningOff ? `${user.name} deactivated` : `${user.name} reactivated`,
      'Could not change the account',
    );
  }

  async function resetPassword() {
    const confirmed = await dialog.confirm({
      title: `Reset the password for ${user.name}?`,
      message: 'A new password is generated and shown once. Every session they have open is ended.',
      confirmLabel: 'Reset password',
    });
    if (!confirmed) return;

    try {
      const result = await api.post<{ password: string }>(`/api/users/${user.id}/reset-password`);
      onPassword(user.email, result.password, 'Password reset');
      onChanged();
    } catch (caught) {
      toast.error(
        'Could not reset the password',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  async function remove() {
    const confirmed = await dialog.confirm({
      title: `Delete ${user.name}?`,
      message:
        'This account has never acted, so it can be removed completely. This cannot be undone.',
      confirmLabel: 'Delete account',
      tone: 'danger',
    });
    if (!confirmed) return;

    await run(
      () => api.delete(`/api/users/${user.id}`),
      `${user.name} deleted`,
      'Could not delete',
    );
  }

  async function changeAccess() {
    // Loaded when the dialog opens rather than with the row: a table of
    // twenty accounts would otherwise make twenty requests for something
    // nobody has asked to see.
    let current: GrantPayload & { itemDetails: PickableItem[] };
    try {
      current = await api.get<GrantPayload & { itemDetails: PickableItem[] }>(
        `/api/users/${user.id}/access`,
      );
    } catch {
      toast.error('Could not load what they can see', 'Please try again.');
      return;
    }

    // The names come back with the ids so the chips can be drawn without a
    // second round of lookups — a selection shown as "3 items" is not one
    // anybody can check.
    const initial: AccessSelection = {
      folders: new Set(current.folderIds),
      items: new Map(current.itemDetails.map((item) => [itemKey(item), item])),
    };

    dialog.open(
      (close) => (
        <AccessDialog
          name={user.name}
          initial={initial}
          onClose={close}
          onSave={async (selection) => {
            await run(
              () => api.post(`/api/users/${user.id}/access`, payloadOf(selection)),
              `Updated what ${user.name} can see`,
              'Could not change their access',
            );
            close();
          }}
        />
      ),
      // Two columns of Drive, not a form.
      { wide: true },
    );
  }

  const items: MenuItem[] = [
    {
      label: 'What they can see',
      icon: <Icons.Folder />,
      // An Owner is not governed by grants; there is nothing here to change.
      disabled: user.role === 'OWNER',
      onSelect: () => void changeAccess(),
    },
    {
      label: 'Reset password',
      icon: <KeyGlyph />,
      onSelect: () => void resetPassword(),
    },
    {
      label: 'Sign out everywhere',
      icon: <Icons.Check />,
      disabled: user.activeSessions === 0,
      onSelect: () =>
        void run(
          () => api.post(`/api/users/${user.id}/revoke-sessions`),
          `${user.name} signed out everywhere`,
          'Could not sign them out',
        ),
    },
    {
      label: user.isActive ? 'Deactivate' : 'Reactivate',
      icon: <Icons.Check />,
      disabled: isMe || (user.isActive && isLastOwner),
      tone: user.isActive ? 'danger' : 'default',
      onSelect: () => void toggleActive(),
      separatorBefore: true,
    },
    {
      label: user.canDelete ? 'Delete account' : 'Delete (has history)',
      icon: <Icons.Trash />,
      // Disabled rather than hidden: knowing *why* an account cannot be
      // removed is more useful than the option quietly not being there.
      disabled: !user.canDelete || isMe || isLastOwner,
      tone: 'danger',
      onSelect: () => void remove(),
    },
  ];

  return (
    <>
      <tr
        className={cx(
          'group h-row border-b border-border transition-fast last:border-0 hover:bg-blue-50',
          !user.isActive && 'opacity-60',
        )}
        onContextMenu={menu.openAtPointer}
      >
        <td className="px-4">
          <div className="flex items-center gap-2">
            <span className="text-body-sm font-medium text-ink">{user.name}</span>
            {isMe ? (
              <span className="rounded-full bg-[var(--blue-50)] px-2 py-0.5 text-micro text-[var(--blue-700)]">
                You
              </span>
            ) : null}
          </div>
          <span className="text-micro text-muted">{user.email}</span>
        </td>

        <td className="px-4">
          <Tooltip
            label={ROLE_LABELS[user.role]}
            detail={
              isLastOwner
                ? 'The only active owner. Promote someone else before changing this.'
                : ROLE_DESCRIPTIONS[user.role]
            }
          >
            <select
              value={user.role}
              disabled={isLastOwner}
              onChange={(event) => void changeRole(event.target.value as Role)}
              aria-label={`Role for ${user.name}`}
              className="h-8 w-full rounded-md border border-border-input bg-surface px-2 text-body-sm text-ink disabled:cursor-not-allowed disabled:text-muted"
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </Tooltip>
        </td>

        <td className="px-4">
          <div className="flex flex-wrap items-center gap-1">
            <span
              className={cx(
                'rounded-full px-2 py-0.5 text-micro font-medium',
                user.isActive
                  ? 'bg-success-bg text-[var(--success)]'
                  : 'bg-sunken text-muted line-through',
              )}
            >
              {user.isActive ? 'Active' : 'Inactive'}
            </span>
            {user.totpEnabled ? (
              <Tooltip label="Two-factor enabled">
                <span className="rounded-full bg-[var(--blue-50)] px-2 py-0.5 text-micro text-[var(--blue-700)]">
                  2FA
                </span>
              </Tooltip>
            ) : null}
          </div>
        </td>

        <td className="px-4 text-body-sm text-muted" data-numeric>
          {user.lastLoginAt ? (
            <Tooltip label={formatDateTime(user.lastLoginAt)}>
              <span>{formatRelative(user.lastLoginAt)}</span>
            </Tooltip>
          ) : (
            <span className="text-subtle">Never</span>
          )}
          {user.activeSessions > 0 ? (
            <span className="ml-2 text-micro text-[var(--success)]">
              {user.activeSessions} signed in
            </span>
          ) : null}
        </td>

        <td className="px-4">
          <div className="flex justify-end">
            {/* No tooltip on this one. It is the only control on the row and
                its meaning is not in doubt, so a dark panel covering the rows
                beside it every time the pointer crosses the column is noise
                rather than help. The button still names itself for a screen
                reader, which is what the tooltip was carrying that mattered. */}
            <button
              type="button"
              aria-label={`Actions for ${user.name}`}
              onClick={menu.openUnder}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted hover:bg-sunken hover:text-ink"
            >
              <KebabGlyph />
            </button>
          </div>
        </td>
      </tr>

      {menu.position ? (
        <ContextMenu items={items} position={menu.position} onClose={menu.close} />
      ) : null}
    </>
  );
}

function InviteDialog({
  onSubmit,
  onClose,
}: {
  onSubmit(input: { email: string; name: string; role: Role } & GrantPayload): void;
  onClose(): void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('STAFF');
  const [selection, setSelection] = useState<AccessSelection>(EMPTY_SELECTION);

  // An Owner sees the whole Drive and is not governed by grants, so there is
  // nothing to choose — and offering the choice would suggest otherwise.
  const picksFolders = role !== 'OWNER';

  return (
    <div>
      <div className="border-b border-border px-5 pb-3 pt-5">
        <h2 className="text-h3 text-ink">Add someone</h2>
        <p className="mt-1 text-body-sm text-body">
          A password is generated and shown once for you to hand over. They confirm their own
          timezone the first time they sign in.
        </p>
      </div>

      {/*
        Two columns once there is a Drive to choose from, one while there is
        not. Who the account is and what it can see are separate decisions and
        both are long: stacked, the folders sat far enough below the role that
        picking one meant losing sight of the other. An Owner picks nothing, so
        for them the second column would be an empty half of a dialog.
      */}
      <div
        className={cx(
          'gap-x-6 gap-y-3 px-5 py-4',
          picksFolders ? 'grid grid-cols-1 items-start sm:grid-cols-2' : 'space-y-3',
        )}
      >
        <div className="space-y-3">
          <label className="block">
            <span className="text-meta font-medium text-body">Full name</span>
            <input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-1 h-control w-full rounded-md border border-border-input px-3 text-body-sm text-ink"
            />
          </label>

          <label className="block">
            <span className="text-meta font-medium text-body">Email</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-1 h-control w-full rounded-md border border-border-input px-3 text-body-sm text-ink"
            />
          </label>

          <fieldset>
            <legend className="text-meta font-medium text-body">Role</legend>
            <div className="mt-1 space-y-1">
              {ROLES.map((option) => (
                <label
                  key={option}
                  className={cx(
                    'flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2 transition-fast',
                    role === option
                      ? 'border-[var(--blue-600)] bg-[var(--blue-50)]'
                      : 'border-border hover:bg-sunken',
                  )}
                >
                  <input
                    type="radio"
                    name="role"
                    checked={role === option}
                    onChange={() => setRole(option)}
                    className="mt-1 h-4 w-4 accent-[var(--blue-600)]"
                  />
                  <span>
                    <span className="block text-body-sm font-medium text-ink">
                      {ROLE_LABELS[option]}
                    </span>
                    <span className="block text-micro text-muted">{ROLE_DESCRIPTIONS[option]}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        {picksFolders ? (
          <fieldset className="min-w-0">
            <legend className="text-meta font-medium text-body">What they can see</legend>
            <p className="mb-1 mt-0.5 text-micro text-muted">
              Whole folders, and single files out of folders you would rather not open up. They see
              what you choose here and nothing else in the Drive.
            </p>
            <AccessPicker selection={selection} onChange={setSelection} />
          </fieldset>
        ) : null}
      </div>

      <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          disabled={!name.trim() || !email.trim()}
          onClick={() =>
            onSubmit({
              email: email.trim(),
              name: name.trim(),
              role,
              ...(picksFolders ? payloadOf(selection) : { folderIds: [], items: [] }),
            })
          }
        >
          Create account
        </Button>
      </div>
    </div>
  );
}

/**
 * Changing what an existing account can see.
 *
 * The same picker the account was made with, so the two screens cannot drift
 * into disagreeing about what a grant means.
 */
function AccessDialog({
  name,
  initial,
  onSave,
  onClose,
}: {
  name: string;
  initial: AccessSelection;
  onSave(selection: AccessSelection): void;
  onClose(): void;
}) {
  const [selection, setSelection] = useState<AccessSelection>(initial);

  return (
    <div>
      <div className="border-b border-border px-5 pb-3 pt-5">
        <h2 className="text-h3 text-ink">What {name} can see</h2>
        <p className="mt-1 text-body-sm text-body">
          Whole folders, and single files out of folders you would rather not open up. Taking
          something away takes it away the next time they load the Drive.
        </p>
      </div>

      <div className="px-5 py-4">
        <AccessPicker selection={selection} onChange={setSelection} />
      </div>

      <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={() => onSave(selection)}>
          Save
        </Button>
      </div>
    </div>
  );
}

function Th({
  children,
  align = 'left',
  className = '',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={cx(
        'px-4 py-2.5 text-micro font-semibold uppercase tracking-wide text-muted',
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

function KebabGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <circle cx="8" cy="3.5" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="12.5" r="1.4" />
    </svg>
  );
}

function KeyGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
    >
      <circle cx="5.5" cy="5.5" r="3" />
      <path d="M7.6 7.6 13 13M11 11l1.5-1.5M12.5 12.5 14 11" strokeLinecap="round" />
    </svg>
  );
}
